import { useState } from 'react'
import { Button, DatePicker, Input, Modal } from '@bitcine/cinesend-theme'
import useStore from '../../hooks/use_store'
import { useNavigate } from 'react-router-dom'
import { indefiniteEndDate, indefiniteStartDate } from '../../constants/dates'
import { CycleStatus } from '../../constants/cycle_statuses'
import { GROUP_ASSET_TYPES } from '../../constants/group_asset_types'

export default function CreateAssetButton({ cycle, assetType, assetLabel }) {
  const cycleID = cycle.id
  const [createModal, setCreateModal] = useState(null)
  const [title, setTitle] = useState()
  const { createModel } = useStore()
  const navigate = useNavigate()
  const [startDate, setStartDate] = useState(cycle.start_date)
  const [endDate, setEndDate] = useState(null)
  // Group asset dates are derived from their children at snapshot time, so they aren't set on create
  const isGroupAsset = GROUP_ASSET_TYPES.includes(assetType)
  const showDateFields = assetType !== 'ad' && createModal !== 'warning-slate' && !isGroupAsset

  const createAsset = () => {
    createModel.mutate(
      {
        endpoint: `cycles/${cycleID}/assets`,
        data: {
          cycle_id: cycleID,
          title,
          asset_type: assetType,
          // Group dates are recalculated from children at snapshot time; send the cycle range as a placeholder
          start_date: isGroupAsset ? cycle.start_date : startDate,
          end_date: isGroupAsset ? cycle.end_date : endDate,
          ad_type: createModal === true ? null : createModal,
        },
      },
      {
        onSuccess: (res) => {
          setTitle('')
          setCreateModal(false)
          if (res.data.asset) {
            navigate(`${res.data.asset.id}`)
          }
        },
      }
    )
  }

  const isOpenCycle = cycle.status === CycleStatus.Open

  const adButtons = [
    {
      label: 'Create Warning Slate',
      adType: 'warning-slate',
      startDate: indefiniteStartDate,
      endDate: indefiniteEndDate,
    },
    { label: 'Create Preroll Ad', adType: 'preroll-ad', startDate: cycle.start_date, endDate: cycle.end_date },
    {
      label: 'Create Interstitial Ad',
      adType: 'interstitial-ad',
      startDate: cycle.start_date,
      endDate: cycle.end_date,
    },
  ]

  return (
    <>
      {assetType === 'ad' ? (
        <>
          {adButtons.map(({ label, adType, startDate, endDate }) => (
            <Button
              key={adType}
              icon='add_circle_outline'
              disabled={!isOpenCycle}
              title={!isOpenCycle ? 'Creating assets is only allowed on open cycles' : undefined}
              onClick={() => {
                setStartDate(startDate)
                setEndDate(endDate)
                setCreateModal(adType)
              }}
            >
              {label}
            </Button>
          ))}
        </>
      ) : (
        <Button
          onClick={() => setCreateModal(true)}
          icon='add_circle_outline'
          disabled={!isOpenCycle}
          title={!isOpenCycle ? 'Creating assets is only allowed on open cycles' : undefined}
        >
          Create
        </Button>
      )}
      {createModal && (
        <Modal
          header={`Create ${assetLabel}`}
          onClose={() => setCreateModal(false)}
          confirmButton={{
            text: `Create ${assetLabel}`,
            onClick: () => {
              createAsset()
            },
            disabled: !title || (showDateFields && (!startDate || !endDate)) || createModel.isLoading,
          }}
        >
          <div className='space-y-4'>
            <Input
              label={`Title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => e.charCode === 13 && !!e.target.value && createAsset()}
              placeholder={`Enter new title here...`}
              autoFocus
            />
            {showDateFields && (
              <>
                <DatePicker
                  useLocal={false}
                  label={`Start Date`}
                  date={startDate}
                  dateFormat={'yyyy-MM-dd'}
                  showTimeSelect={false}
                  onChange={(newDate) => setStartDate(newDate)}
                />
                <div
                  onClick={() => {
                    if (!endDate) {
                      setEndDate(startDate)
                    }
                  }}
                >
                  <DatePicker
                    label={`End Date`}
                    useLocal={false}
                    date={endDate}
                    dateFormat={'yyyy-MM-dd'}
                    showTimeSelect={false}
                    onChange={(newDate) => setEndDate(newDate)}
                  />
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}
